import React from "react"
import { Link } from "gatsby"
import { Linkedin, Twitter, Mail, ArrowRight, MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

const footerServices = [
  { label: "AI Strategy & Consulting", href: "/services/ai-strategy" },
  { label: "Generative AI Solutions", href: "/services/generative-ai" },
  { label: "Data & Analytics", href: "/services/data-analytics" },
  { label: "Cloud Transformation", href: "/services/cloud" },
  { label: "Cybersecurity", href: "/services/cybersecurity" },
  { label: "Digital Engineering", href: "/services/digital-engineering" },
]

const footerIndustries = [
  { label: "Banking & Financial Services", href: "/industries/banking" },
  { label: "Healthcare & Life Sciences", href: "/industries/healthcare" },
  { label: "Retail & Consumer Goods", href: "/industries/retail" },
  { label: "Energy & Utilities", href: "/industries/energy" },
  { label: "Manufacturing", href: "/industries/manufacturing" },
  { label: "Technology", href: "/industries/technology" },
]

const footerCompany = [
  { label: "About Us", href: "/about" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Careers", href: "/careers" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
]

const footerResources = [
  { label: "AI Research", href: "/resources/ai-research" },
  { label: "Whitepapers", href: "/resources/whitepapers" },
  { label: "Webinars", href: "/resources/webinars" },
  { label: "Partners", href: "/partners" },
]

const globalOffices = [
  "New York",
  "London",
  "Bangalore",
  "Singapore",
  "Dubai",
]

export const Footer = () => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="border-t border-border bg-muted/30">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-1">
                Subscribe to our AI Insights Newsletter
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Stay ahead with the latest enterprise AI trends, research, and
                transformation strategies delivered to your inbox.
              </p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Enter your work email"
                className="bg-background border-border text-foreground md:w-72"
              />
              <Button className="bg-gradient-primary text-white border-0 shrink-0">
                Subscribe <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Grid */}
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 mb-14">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="font-bold text-xl text-foreground">
                Shailka Systems
              </span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xs">
              Shailka Systems is a global enterprise AI services company helping
              organizations harness the power of artificial intelligence to
              transform their business.
            </p>
            <div className="flex gap-3">
              <a
                href="https://linkedin.com/company/shailka"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://twitter.com/shailka"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
              >
                <Twitter size={18} />
              </a>
              <a
                href="mailto:info@shailka.com"
                aria-label="Email"
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">
              Services
            </h4>
            <ul className="space-y-3">
              {footerServices.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries Column */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">
              Industries
            </h4>
            <ul className="space-y-3">
              {footerIndustries.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">
              Company
            </h4>
            <ul className="space-y-3">
              {footerCompany.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerResources.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Global Offices */}
        <div className="border-t border-border pt-8 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground shrink-0">
              <MapPin size={16} className="text-primary" />
              Global Offices
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {globalOffices.map((office) => (
                <span
                  key={office}
                  className="text-sm text-muted-foreground"
                >
                  {office}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {currentYear} Shailka Systems Private Limited. All rights
              reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy-policy"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookie-policy"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
